import { createSelector } from 'reselect';
import { Map, List } from 'immutable';

import { filterFacilitiesList } from 'utils/FacilitiesFilterHelper';
import { denormalizeModelList } from '../../utils/Normalizer';

const selectMonitorWizzard = (state) => state.getIn(['monitor', 'wizzard']);

const makeSelectById = () =>
  createSelector(selectMonitorWizzard, (state) => state.get('byId', Map()));

const makeSelectAvailableAccounts = () =>
  createSelector(selectMonitorWizzard, (state) => state.get('availableAccounts', Map()));

const makeSelectFacilitiesMap = () =>
  createSelector(selectMonitorWizzard, (state) => state.get('facilities', Map()));

const makeActiveEditFacilityId = () =>
  createSelector(selectMonitorWizzard, (state) => state.get('activeEditFacilityId'));

const makeSelectAvailableAccountsList = () =>
  createSelector(makeSelectById(), makeSelectAvailableAccounts(), (byId, state) =>
    denormalizeModelList(byId, state.get('ids', List())),
  );

const makeSelectAvailableBankAccountsList = () =>
  createSelector(makeSelectAvailableAccountsList(), (list) =>
    list.filter((account) => account.isBankAccount && !account.isCreditLine),
  );

const makeSelectAvailableFilteredBankAccountsList = () =>
  createSelector(makeSelectAvailableBankAccountsList(), (list) => filterFacilitiesList(list));

const makeSelectAvailableCreditCardsList = () =>
  createSelector(makeSelectAvailableAccountsList(), (list) =>
    list.filter((account) => account.isCreditCard),
  );

const makeSelectAvailableFilteredCreditLCardsList = () =>
  createSelector(makeSelectAvailableCreditCardsList(), (list) => filterFacilitiesList(list));

const makeSelectAvailableCreditLinesList = () =>
  createSelector(makeSelectAvailableAccountsList(), (list) =>
    list.filter((account) => account.isCreditLine),
  );

const makeSelectAvailableFilteredCreditLinesList = () =>
  createSelector(makeSelectAvailableCreditLinesList(), (list) => filterFacilitiesList(list));

const makeSelectAvailableLedgerAccountsList = () =>
  createSelector(makeSelectAvailableAccountsList(), (list) =>
    list.filter((account) => !account.isBankAccount),
  );

const makeSelectAvailableLedgerAccountsListSorted = () =>
  createSelector(makeSelectAvailableLedgerAccountsList(), (list) =>
    list.sort((a, b) => a.accountIdFormatted.localeCompare(b.accountIdFormatted)),
  );

const makeSelectAvailableAccountsLoading = () =>
  createSelector(makeSelectAvailableAccounts(), (state) =>
    state.getIn(['network', 'loading'], false),
  );

const makeSelectAvailableAccountsError = () =>
  createSelector(makeSelectAvailableAccounts(), (state) =>
    state.getIn(['network', 'error'], false),
  );

const makeSelectActiveLenders = () =>
  createSelector(selectMonitorWizzard, (state) => state.getIn(['activeLenders', 'data'], List()));

const makeSelectActiveLendersSorted = () =>
  createSelector(makeSelectActiveLenders(), (list) =>
    list.sort((a, b) => a.name.localeCompare(b.name)),
  );

const makeSelectFundingSearchesList = () =>
  createSelector(selectMonitorWizzard, (state) => state.getIn(['fundingSearches', 'data'], List()));

const makeSelectAvailableFilteredFundingSearchesList = () =>
  createSelector(makeSelectFundingSearchesList(), (list) => filterFacilitiesList(list));

const makeSelectFundingSearchesLoading = () =>
  createSelector(selectMonitorWizzard, (state) =>
    state.getIn(['fundingSearches', 'network', 'loading'], false),
  );

const makeSelectFundingSearchesError = () =>
  createSelector(selectMonitorWizzard, (state) =>
    state.getIn(['fundingSearches', 'network', 'error'], false),
  );

const makeSelectMonitorWizzardClient = () =>
  createSelector(selectMonitorWizzard, (state) => state.get('client', Map()));

const makeSelectMonitorWizzardAccountingSolution = () =>
  createSelector(selectMonitorWizzard, (state) => state.get('accountingSolution', ''));

const makeSelectMonitorWizzardDataLoadingIsOpen = () =>
  createSelector(selectMonitorWizzard, (state) => state.get('isDataLoadingModalOpen', false));

const makeSelectMonitorWizzardDataLoadingErrorIsOpen = () =>
  createSelector(selectMonitorWizzard, (state) => state.get('isErrorLoadingModalOpen', false));

const makeSelectCreditLinesFacilityMap = () =>
  createSelector(selectMonitorWizzard, (state) => state.get('creditLinesFacilityMap', Map()));

const makeSelectCreditLinesFacilityAccounts = () =>
  createSelector(makeSelectCreditLinesFacilityMap(), (state) => state.get('accounts', List()));

const makeSelectProviderMapValid = () =>
  createSelector(makeSelectCreditLinesFacilityMap(), (state) => state.get('valid', false));

const makeSelectIntegrationFilePayload = () =>
  createSelector(selectMonitorWizzard, (state) => state.get('integrationFilePayload', null));

// ================================================== connectWizzard

const makeSelectMonitorConnect = () =>
  createSelector(selectMonitorWizzard, (state) => state.get('connectWizzard', Map()));

const makeSelectMonitorWizzardIsOpen = () =>
  createSelector(makeSelectMonitorConnect(), (state) => state.get('isOpen', false));

const makeSelectMonitorWizzardStartPath = () =>
  createSelector(makeSelectMonitorConnect(), (state) => state.get('startPath', false));

const makeSelectMonitorWizzardOptions = () =>
  createSelector(makeSelectMonitorConnect(), (state) => state.get('options', Map()));

const makeSelectMonitorWizzardConnectionHistory = () =>
  createSelector(selectMonitorWizzard, (state) => state.get('connectionHistoryById', Map()));

const makeSelectMonitorWizzardConnectionHistoryItem = (entityId) =>
  createSelector(selectMonitorWizzard, (state) =>
    state.getIn(['connectionHistoryById', entityId], null),
  );

// ================================================== addNewClient

const makeSelectMonitorAddNewClient = () =>
  createSelector(selectMonitorWizzard, (state) => state.get('addNewClient', Map()));

const makeSelectAddNewClientCompaniesById = () =>
  createSelector(makeSelectMonitorAddNewClient(), (state) => state.get('byId', Map()));

const makeSelectAddNewClientCompanies = () =>
  createSelector(makeSelectMonitorAddNewClient(), (state) => state.get('companies', Map()));

const makeSelectAddNewClientCompaniesList = () =>
  createSelector(
    makeSelectAddNewClientCompaniesById(),
    makeSelectAddNewClientCompanies(),
    (byId, state) => denormalizeModelList(byId, state.get('ids', List())),
  );

export {
  selectMonitorWizzard,
  makeSelectById,
  makeSelectActiveLenders,
  makeSelectActiveLendersSorted,
  makeSelectAvailableAccountsList,
  makeSelectFacilitiesMap,
  makeActiveEditFacilityId,
  makeSelectAvailableBankAccountsList,
  makeSelectAvailableCreditLinesList,
  makeSelectAvailableLedgerAccountsList,
  makeSelectAvailableLedgerAccountsListSorted,
  makeSelectAvailableAccountsLoading,
  makeSelectAvailableAccountsError,
  makeSelectFundingSearchesList,
  makeSelectFundingSearchesLoading,
  makeSelectFundingSearchesError,
  makeSelectMonitorWizzardIsOpen,
  makeSelectMonitorWizzardClient,
  makeSelectMonitorWizzardDataLoadingIsOpen,
  makeSelectMonitorWizzardDataLoadingErrorIsOpen,
  makeSelectMonitorWizzardAccountingSolution,
  makeSelectMonitorWizzardConnectionHistory,
  makeSelectMonitorWizzardConnectionHistoryItem,
  makeSelectCreditLinesFacilityAccounts,
  makeSelectProviderMapValid,
  makeSelectMonitorWizzardStartPath,
  makeSelectMonitorWizzardOptions,
  makeSelectAddNewClientCompaniesList,
  makeSelectAvailableCreditCardsList,
  makeSelectIntegrationFilePayload,
  makeSelectAvailableFilteredCreditLinesList,
  makeSelectAvailableFilteredCreditLCardsList,
  makeSelectAvailableFilteredBankAccountsList,
  makeSelectAvailableFilteredFundingSearchesList,
};
